import React, { useMemo, useState } from "react";
import { MarkdownSyntaxHighlighter } from "../../MarkdownSyntaxHighlighter";
import {
  MessageContainer,
  MessageContent,
  BotMessageText,
  WidgetArtifactsContainer,
} from "../styles";
import { useCopyText } from "@shared/hooks/useCopyText";
import {
  extractReasoningInfo,
  getArtifactDisplayName,
  getFormattedMessageText,
  getToolCallActionLabel,
} from "../helpers";
import { MessageRequestDuration } from "../../MessageRequestDuration";
import { MessageLoading } from "../../MessageLoading";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Tooltip } from "@mui/material";
import {
  TimelineDot,
  CodeBlockWrapper,
  CopyIconButton,
} from "./BotMessage.styles";
import { ChatTimeline, ChatTimelineItem } from "./ChatTimeline";
import {
  TimelineDoneContent,
  TimelineDoneText,
  TimelineActionLabel,
} from "./ChatTimeline.styles";
import { AccordionMessage } from "./AccordionMessage";
import { useAuthStore } from "@store/auth.store";
import { ChatMessage, ChatMessageArtifact } from "@store/messages.store";
import { WidgetArtifactCard } from "../../WidgetArtifactCard/WidgetArtifactCard";

type Props = {
  id: string;
  text?: string;
  customContent?: React.ReactNode;
  authorName: string;
  authorAvatar: string;
  type: string;
  subMessages?: Omit<Props, "subMessages">[];
  artifacts?: ChatMessage["artifacts"];
  // This is true while the current message hast not started its "final response" stream event yet
  isPreparingMessageResponse?: boolean;
  defaultTimelineOpen?: boolean;
  onTimelineOpenChange?: (isOpen: boolean) => void;
  _metadata?: {
    elapsedTime: number;
  };
  onArtifactClick?: (artifact: ChatMessageArtifact) => void;
};

type ParsedJsonMessageType = {
  input?: { operationData?: { operationName?: string } };
  arguments?: { operationData?: { operationName?: string } };
};

export const BotMessage = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const {
      id,
      text,
      customContent,
      type,
      isPreparingMessageResponse,
      defaultTimelineOpen,
      onTimelineOpenChange,
      subMessages,
      _metadata,
      artifacts,
      onArtifactClick,
    } = props;
    const [isTimelineOpen, setIsTimelineOpen] = useState(
      defaultTimelineOpen ?? false,
    );
    const { handleCopyText, isCopied } = useCopyText();
    const { showChatDebugMessages: isDebugMessageEnabled } = useAuthStore(
      (state) => state.preferences,
    );

    const hasSubMessage =
      subMessages?.some(
        (m) =>
          ((m.id.includes("rs_") || m.type === "reasoning") && !!m.text) ||
          (m.type === "debug-json" && !!m.text),
      ) ||
      artifacts?.some((a) => a.kind === "reasoning") ||
      artifacts?.some((a) => a.kind === "tool-call-request") ||
      (isDebugMessageEnabled && artifacts?.some((a) => a.kind !== "reasoning"));

    const currentActiveStepTitle = useMemo(() => {
      if (!isPreparingMessageResponse) return "Context";

      if (artifacts?.length) {
        for (let i = artifacts.length - 1; i >= 0; i--) {
          const artifact = artifacts[i];
          if (artifact.kind === "tool-call-request") {
            const toolArtifact = artifact as any;
            const operationName =
              toolArtifact.content?.arguments?.operationData?.operationName ||
              toolArtifact.content?.tool ||
              "";
            return getToolCallActionLabel(operationName);
          }
          if (artifact.kind === "reasoning") {
            const text = (artifact as any).content?.text;
            if (text) {
              const blocks = extractReasoningInfo(text);
              if (blocks.length > 0) return blocks[blocks.length - 1].title;
            }
          }
        }
      }

      if (subMessages?.length) {
        for (let i = subMessages.length - 1; i >= 0; i--) {
          const msg = subMessages[i];
          if (msg.text && msg.type === "debug-json") {
            try {
              const { input, arguments: args }: ParsedJsonMessageType =
                JSON.parse(msg.text);
              const operationName =
                (input || args)?.operationData?.operationName || "";
              return getToolCallActionLabel(operationName);
            } catch {
              // malformed JSON — fall through to next sub-message
            }
          }
          if (
            msg.text &&
            (msg.id.includes("rs_") || msg.type === "reasoning")
          ) {
            const blocks = extractReasoningInfo(msg.text);
            if (blocks.length > 0) return blocks[blocks.length - 1].title;
          }
        }
      }

      return "I'm on it...";
    }, [isPreparingMessageResponse, subMessages, artifacts]);

    const content = useMemo(() => {
      if (type === "image-fallback") {
        return (
          <MessageLoading
            messages={["Generating image"]}
            displayMessageDuration={false}
          />
        );
      }
      if (text) {
        return (
          <MarkdownSyntaxHighlighter
            text={getFormattedMessageText(text, type)}
          />
        );
      }
      if (customContent) {
        return customContent;
      }

      return null;
    }, [customContent, text, type]);

    const messageContent = content && (
      <BotMessageText>
        {content}
        <MessageRequestDuration finishedDuration={_metadata?.elapsedTime} />
      </BotMessageText>
    );

    const buildTimelineEntries = (): ChatTimelineItem[] => {
      const entries: ChatTimelineItem[] = [];

      const regularDot = <TimelineDot />;

      // Index of the last tool call — hidden in outer title while still preparing
      const lastToolCallSubMsgIdx =
        isPreparingMessageResponse && !artifacts?.length
          ? (subMessages?.reduce<number>(
              (last, m, i) => (m.type === "debug-json" && m.text ? i : last),
              -1,
            ) ?? -1)
          : -1;

      const lastToolCallArtifactIdx =
        isPreparingMessageResponse && !!artifacts?.length
          ? artifacts.reduce<number>(
              (last, a, i) => (a.kind === "tool-call-request" ? i : last),
              -1,
            )
          : -1;

      if (!artifacts?.length) {
        subMessages?.forEach((subMessage, i) => {
          if (!subMessage.text) return;
          if (subMessage.type === "debug-json") {
            if (i === lastToolCallSubMsgIdx) return;
            const { input, arguments: args }: ParsedJsonMessageType =
              JSON.parse(subMessage.text);
            const operationName =
              (input || args)?.operationData?.operationName || "";
            if (isDebugMessageEnabled) {
              entries.push({
                key: `${id}-${i}`,
                dot: regularDot,
                content: (
                  <AccordionMessage
                    title={`Debug message - ${operationName || "unmapped"}`}
                  >
                    <CodeBlockWrapper>
                      <MarkdownSyntaxHighlighter
                        text={getFormattedMessageText(
                          subMessage.text,
                          subMessage.type,
                        )}
                      />
                      <Tooltip
                        title={isCopied ? "Copied!" : "Copy"}
                        placement="bottom"
                      >
                        <CopyIconButton
                          size="small"
                          onClick={() => handleCopyText(subMessage.text || "")}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </CopyIconButton>
                      </Tooltip>
                    </CodeBlockWrapper>
                  </AccordionMessage>
                ),
              });
            } else {
              entries.push({
                key: `${id}-${i}`,
                dot: regularDot,
                content: (
                  <TimelineActionLabel>
                    {getToolCallActionLabel(operationName)}
                  </TimelineActionLabel>
                ),
              });
            }
          } else {
            extractReasoningInfo(subMessage.text).forEach(
              (block, blockIndex) => {
                entries.push({
                  key: `${id}-${i}-${blockIndex}`,
                  dot: regularDot,
                  content: (
                    <AccordionMessage title={block.title}>
                      <MarkdownSyntaxHighlighter
                        text={getFormattedMessageText(block.content, "text")}
                      />
                    </AccordionMessage>
                  ),
                });
              },
            );
          }
        });
      } else {
        artifacts.forEach((artifact, i) => {
          if (artifact.kind === "reasoning") {
            const reasoningBlocks = extractReasoningInfo(
              (artifact as any).content.text,
            );
            reasoningBlocks.forEach((block, blockIndex) => {
              entries.push({
                key: `${id}-${i}-${blockIndex}`,
                dot: regularDot,
                content: (
                  <AccordionMessage title={block.title}>
                    <MarkdownSyntaxHighlighter
                      text={getFormattedMessageText(block.content, "text")}
                    />
                  </AccordionMessage>
                ),
              });
            });
          } else if (artifact.kind === "tool-call-request") {
            if (i === lastToolCallArtifactIdx) return;
            const toolArtifact = artifact as any;
            const operationName =
              toolArtifact.content?.arguments?.operationData?.operationName ||
              toolArtifact.content?.tool ||
              "";
            const actionLabel = getToolCallActionLabel(operationName);

            if (isDebugMessageEnabled) {
              const artifactContent = JSON.stringify(artifact, null, 2);
              entries.push({
                key: `${id}-${i}`,
                dot: regularDot,
                content: (
                  <AccordionMessage
                    title={getArtifactDisplayName(artifact, artifacts)}
                  >
                    <CodeBlockWrapper>
                      <MarkdownSyntaxHighlighter
                        text={getFormattedMessageText(
                          artifactContent,
                          "debug-json",
                        )}
                      />
                      <Tooltip
                        title={isCopied ? "Copied!" : "Copy"}
                        placement="bottom"
                      >
                        <CopyIconButton
                          size="small"
                          onClick={() => handleCopyText(artifactContent)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </CopyIconButton>
                      </Tooltip>
                    </CodeBlockWrapper>
                  </AccordionMessage>
                ),
              });
            } else {
              entries.push({
                key: `${id}-${i}`,
                dot: regularDot,
                content: (
                  <TimelineActionLabel>{actionLabel}</TimelineActionLabel>
                ),
              });
            }
          } else if (isDebugMessageEnabled) {
            const artifactContent = JSON.stringify(artifact, null, 2);
            entries.push({
              key: `${id}-${i}`,
              dot: regularDot,
              content: (
                <AccordionMessage
                  title={getArtifactDisplayName(artifact, artifacts)}
                >
                  <CodeBlockWrapper>
                    <MarkdownSyntaxHighlighter
                      text={getFormattedMessageText(
                        artifactContent,
                        "debug-json",
                      )}
                    />
                    <Tooltip
                      title={isCopied ? "Copied!" : "Copy"}
                      placement="bottom"
                    >
                      <CopyIconButton
                        size="small"
                        onClick={() => handleCopyText(artifactContent)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </CopyIconButton>
                    </Tooltip>
                  </CodeBlockWrapper>
                </AccordionMessage>
              ),
            });
          }
        });
      }

      if (!isPreparingMessageResponse) {
        entries.push({
          key: `${id}-done`,
          dot: (
            <CheckCircleIcon
              sx={{
                fontSize: "0.875rem",
                color: "success.main",
                flexShrink: 0,
              }}
            />
          ),
          content: (
            <TimelineDoneContent>
              <TimelineDoneText>Done</TimelineDoneText>
            </TimelineDoneContent>
          ),
        });
      }

      return entries;
    };

    const showOuterAccordion = hasSubMessage || isPreparingMessageResponse;

    const timelineEntries = hasSubMessage ? buildTimelineEntries() : [];

    const subMessagesContent = showOuterAccordion && (
      <AccordionMessage
        title={currentActiveStepTitle}
        finishedReasoning={!isPreparingMessageResponse}
        loading={isPreparingMessageResponse && !hasSubMessage}
        isActive={isPreparingMessageResponse && !!hasSubMessage}
        defaultOpen={isTimelineOpen}
        onExpandChange={(isOpen) => {
          setIsTimelineOpen(isOpen);
          onTimelineOpenChange?.(isOpen);
        }}
      >
        {timelineEntries.length > 0 && <ChatTimeline items={timelineEntries} />}
      </AccordionMessage>
    );

    const isWidgetArtifact = (
      a: ChatMessageArtifact,
    ): a is ChatMessageArtifact & {
      kind: `widget-${string}`;
      title?: string;
    } => a.kind.startsWith("widget-");

    const widgetArtifacts = artifacts?.filter(isWidgetArtifact);

    const widgetCards =
      onArtifactClick && widgetArtifacts?.length
        ? widgetArtifacts.map((artifact, i) => (
            <WidgetArtifactCard
              key={`${artifact.kind}-${i}`}
              artifact={artifact}
              onClick={onArtifactClick}
            />
          ))
        : null;

    if (
      !subMessagesContent &&
      !messageContent &&
      !widgetCards &&
      !isPreparingMessageResponse
    )
      return null;

    return (
      <MessageContainer key={id} ref={ref}>
        <MessageContent>
          {subMessagesContent}
          {messageContent}
          {widgetCards && (
            <WidgetArtifactsContainer>{widgetCards}</WidgetArtifactsContainer>
          )}
        </MessageContent>
      </MessageContainer>
    );
  },
);

BotMessage.displayName = "BotMessage";
